import { treeNodeFilterFunction } from "@/utils/formUtils";
import { DataNode } from "@rc-component/tree-select/es/interface";
import { TreeSelect } from "antd";
import groupBy from "lodash/groupBy";
import { useMemo } from "react";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { EnumerationType } from "../../enums";

const groupFilter = (value: string) => value.startsWith("group-");

type Props = {
  value?: UUID[];
  onChange?: (value: UUID[]) => void;
  allowClear?: boolean;
};

export const CommissionsBatchesTreeSelect = ({ value, onChange, allowClear = true }: Props) => {
  const commissionsBatches = useEnumeration(EnumerationType.COMMISSIONS_BATCHES);

  const treeData = useMemo(() => {
    const groups = groupBy(commissionsBatches, (commission) => commission.name.substring(0, 4));

    return Object.keys(groups).map<DataNode>((group) => ({
      value: `group-${group}`,
      title: group,
      children: groups[group]?.map<DataNode>((commission) => ({
        value: commission.id,
        title: commission.name,
      })),
    }));
  }, [commissionsBatches]);

  return (
    <TreeSelect
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeCheckable
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      allowClear={allowClear}
      showSearch={{ filterTreeNode: treeNodeFilterFunction }}
      treeData={treeData}
      onChange={(data) => {
        if (!data) {
          return;
        }

        const values = data.filter((value) => !groupFilter(value));

        const groupValues = data
          .filter(groupFilter)
          .map((value) => value.split("group-")[1])
          .filter((value): value is string => !!value);

        const result = [
          ...values,
          ...commissionsBatches
            .filter((item) => groupValues.includes(item.name.substring(0, 4)))
            .map((item) => item.id),
        ];

        onChange?.(result);
      }}
      value={value}
    />
  );
};
